import { useBlocker } from "@tanstack/react-router";
import React, { useEffect, useState } from "react";

import { Button } from "./ui/button";
import { Modal } from "./ui/modal/modal";

export const useUnsavedChangesAlert = (isDirty: () => boolean) => {
  const [exitUrl, setExitUrl] = useState<string | undefined>();
  const [showExitAlert, setShowExitAlert] = useState(false);

  const blocker = useBlocker({
    withResolver: true,
    enableBeforeUnload: true,
    shouldBlockFn: async ({ next }) => {
      if (!(await isDirty()) || showExitAlert) {
        return false;
      }
      setExitUrl(next.fullPath);
      setShowExitAlert(true);
      return true;
    },
  });

  useEffect(() => {
    if (!showExitAlert && blocker && typeof blocker.reset === "function") {
      blocker.reset();
    }
  }, [showExitAlert, blocker]);

  const onDiscard = () => {
    setShowExitAlert(false);
    if (blocker && typeof blocker.proceed === "function") {
      blocker.proceed();
    }
  };

  const onCancel = () => {
    setShowExitAlert(false);
  };

  return { showExitAlert, exitUrl, onDiscard, onCancel };
};

export const UnsavedChangesAlert = ({
  show,
  onDiscard,
  onCancel,
}: {
  onDiscard: () => void | Promise<void>;
  onCancel: () => void;
  show: boolean;
}) => {
  return (
    <Modal.Root open={show} onOpenChange={(open) => !open && onCancel()}>
      <Modal.Panel>
        <Modal.Title>Discard unsaved changes?</Modal.Title>
        <Modal.Description>
          You made changes to this page that you haven't saved. If you leave,
          your changes will be lost.
        </Modal.Description>
        <footer className="flex items-center justify-end gap-4">
          <Button variant="ghost" onClick={onCancel}>
            Go Back
          </Button>
          <Button variant="destructive" onClick={onDiscard}>
            Discard Changes
          </Button>
        </footer>
      </Modal.Panel>
    </Modal.Root>
  );
};
